import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import emailTemplateService from '@/shared/services/emailTemplateService';
import {
    usePreviewLock,
    PREVIEW_LOCK_NOTE_ID,
} from '@/shared/hooks/usePreviewLock';
import PreviewLockNote from '@/components/common/PreviewLockNote';
import DecisionEmailPreview from '@/features/applications/faculty/DecisionEmailPreview';
import useUnloadWarning from '@/shared/hooks/useUnloadWarning';
import { readDraft, writeDraft } from '@/shared/utils/drafts';

const TEXT = {
    decision: {
        heading: 'Decision emails',
        loading: 'Loading decision emails...',
        error: "The decision email templates couldn't be loaded.",
        intro: 'When you approve, waitlist or decline an application, the decision dialog fills its message box from the template below for that decision. You can change the text in the dialog before you confirm, and the dialog shows the email as faculty will receive it. A field with no value for an application is left out.',
        previewEmpty:
            'A field with no value for an application is left out of the real email.',
    },
    offer: {
        heading: 'Offer email',
        loading: 'Loading the offer email...',
        error: "The offer email template couldn't be loaded.",
        intro: 'When you or a faculty member make an offer, the offer dialog fills its message box from the template below. The reviewer can change the text before making the offer, and the dialog shows the email as the student will receive it. A field with no value for a position is left out.',
        previewEmpty:
            'A field with no value for a position is left out of the real email.',
    },
    onboarding: {
        heading: 'Onboarding email',
        loading: 'Loading the onboarding email...',
        error: "The onboarding email template couldn't be loaded.",
        intro: 'When you send the onboarding email to a student who accepted an offer, the send dialog fills its message box from the template below. You can change the text for that student before you send, and the dialog shows who it goes to. A field with no value for a student is left out.',
        previewEmpty:
            'A field with no value for a student is left out of the real email.',
    },
};

const draftKeyOf = (programType, kind, key) =>
    `template.${programType}.${kind}.${key}`;

const formatEdited = (entry) => {
    const date = new Date(entry.updatedAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'America/Denver',
    });
    return entry.updatedBy?.name
        ? `Edited ${date} by ${entry.updatedBy.name}`
        : `Edited ${date}`;
};

/**
 * Edits one kind of program email: the decision emails (five templates) or
 * the offer email. Each template is the text the dialog fills in after the
 * sentence that states the decision or offer, which the reviewer can still
 * change before it is recorded. Several templates are edited one at a time,
 * chosen from a list, so the section has one Save in view. Text changed
 * but not saved is kept as a draft per template, so choosing another
 * settings category or leaving the page and coming back finds it again.
 */
function DecisionEmailTemplates({ programType, kind = 'decision' }) {
    const text = TEXT[kind];
    const previewLocked = usePreviewLock();
    const [data, setData] = useState(null);
    const [bodies, setBodies] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    // Per template: { kind: 'saving' | 'saved' | 'reset' | 'error', text }
    const [notes, setNotes] = useState({});
    const [insertKey, setInsertKey] = useState({});
    // With several templates, the one being edited; the others keep their
    // text, saved or not, until the person comes back to them
    const [shownKey, setShownKey] = useState(null);
    const boxes = useRef({});
    const saveButtons = useRef({});
    // The template whose Save button had focus when it was pressed
    const refocusSave = useRef(null);
    const caret = useRef(null);
    // The template last previewed; kept after closing so the title doesn't
    // change while the dialog fades out
    const [previewKey, setPreviewKey] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewEmails, setPreviewEmails] = useState([]);
    // 'loading' | 'ready' | 'error' | 'locked', as DecisionEmailPreview takes it
    const [previewStatus, setPreviewStatus] = useState('loading');
    // The server's reason for refusing the text, such as an unknown field
    const [previewError, setPreviewError] = useState(null);
    // An answer older than the last preview opened or closed is dropped
    const previewRequest = useRef(0);
    const draftKey = (key) => draftKeyOf(programType, kind, key);

    useEffect(() => {
        let ignore = false;
        setLoading(true);
        setLoadError(false);
        setData(null);
        emailTemplateService
            .getTemplates(programType, kind)
            .then((answer) => {
                if (ignore) return;
                setData(answer);
                setBodies(
                    Object.fromEntries(
                        answer.templates.map((t) => [
                            t.key,
                            readDraft(draftKeyOf(programType, kind, t.key)) ??
                                t.body,
                        ])
                    )
                );
            })
            .catch(() => {
                if (!ignore) setLoadError(true);
            })
            .finally(() => {
                if (!ignore) setLoading(false);
            });
        return () => {
            ignore = true;
        };
    }, [programType, kind]);

    // The caret can only be placed, and the box focused, once the new text is
    // in it
    useEffect(() => {
        if (!caret.current) return;
        const { key, position } = caret.current;
        caret.current = null;
        const box = boxes.current[key];
        if (!box) return;
        box.focus();
        box.setSelectionRange(position, position);
    }, [bodies]);

    // Save is disabled while it runs, which drops keyboard focus to the page;
    // once the note settles, the button gets it back, unless the person has
    // already moved focus somewhere else while the save was running
    useEffect(() => {
        const key = refocusSave.current;
        if (!key || notes[key]?.kind === 'saving') return;
        refocusSave.current = null;
        const active = document.activeElement;
        const focusWasLost =
            !active ||
            active === document.body ||
            active === saveButtons.current[key];
        if (focusWasLost) saveButtons.current[key]?.focus();
    }, [notes]);

    const openPreview = async (key) => {
        const request = ++previewRequest.current;
        setPreviewKey(key);
        setPreviewOpen(true);
        setPreviewEmails([]);
        setPreviewError(null);
        if (previewLocked) {
            setPreviewStatus('locked');
            return;
        }
        setPreviewStatus('loading');
        try {
            const answer = await emailTemplateService.previewTemplate(
                programType,
                kind,
                key,
                bodies[key] ?? ''
            );
            if (request !== previewRequest.current) return;
            if (typeof answer?.html !== 'string') throw new Error('shape');
            setPreviewEmails([answer]);
            setPreviewStatus('ready');
        } catch (err) {
            if (request !== previewRequest.current) return;
            // A refused text names what is wrong with it, as a save would
            setPreviewError(
                err.response?.status === 400
                    ? err.response.data?.message || null
                    : null
            );
            setPreviewStatus('error');
        }
    };

    const closePreview = () => {
        previewRequest.current += 1;
        setPreviewOpen(false);
    };

    const setNote = (key, note) =>
        setNotes((prev) => ({ ...prev, [key]: note }));

    const edit = (key, text) => {
        const saved = data.templates.find((t) => t.key === key)?.body ?? '';
        writeDraft(draftKey(key), text === saved ? null : text);
        setBodies((prev) => ({ ...prev, [key]: text }));
        setNote(key, null);
    };

    const replaceEntry = (entry) => {
        setData((prev) => ({
            ...prev,
            templates: prev.templates.map((t) =>
                t.key === entry.key ? entry : t
            ),
        }));
        writeDraft(draftKey(entry.key), null);
        setBodies((prev) => ({ ...prev, [entry.key]: entry.body }));
    };

    const insertField = (key, fieldKey) => {
        const box = boxes.current[key];
        const text = bodies[key] ?? '';
        const start = box?.selectionStart ?? text.length;
        const end = box?.selectionEnd ?? text.length;
        const token = `{{${fieldKey}}}`;
        caret.current = { key, position: start + token.length };
        edit(key, text.slice(0, start) + token + text.slice(end));
    };

    const save = async (key) => {
        if (document.activeElement === saveButtons.current[key]) {
            refocusSave.current = key;
        }
        setNote(key, { kind: 'saving' });
        try {
            const entry = await emailTemplateService.saveTemplate(
                programType,
                kind,
                key,
                bodies[key]
            );
            replaceEntry(entry);
            setNote(key, { kind: 'saved' });
        } catch (err) {
            setNote(key, {
                kind: 'error',
                text:
                    err.response?.data?.message ||
                    'This template could not be saved.',
            });
        }
    };

    const reset = async (key) => {
        setNote(key, { kind: 'saving' });
        try {
            const entry = await emailTemplateService.resetTemplate(
                programType,
                kind,
                key
            );
            // The default entry has no Reset button, so the one that had
            // focus is gone; the box keeps keyboard users in this template
            caret.current = { key, position: (entry.body ?? '').length };
            replaceEntry(entry);
            setNote(key, { kind: 'reset' });
        } catch (err) {
            setNote(key, {
                kind: 'error',
                text:
                    err.response?.data?.message ||
                    'This template could not be reset.',
            });
        }
    };

    const unsaved = (entry) => (bodies[entry.key] ?? '') !== (entry.body ?? '');
    useUnloadWarning(Boolean(data?.templates?.some(unsaved)));

    const discard = (entry) => {
        writeDraft(draftKey(entry.key), null);
        caret.current = { key: entry.key, position: (entry.body ?? '').length };
        setBodies((prev) => ({ ...prev, [entry.key]: entry.body }));
        setNote(entry.key, null);
    };

    if (loading) {
        return (
            <div className="text-center py-4" role="status">
                <Spinner animation="border" size="sm" className="me-2" />
                {text.loading}
            </div>
        );
    }
    if (loadError || !data) {
        return <Alert variant="danger">{text.error}</Alert>;
    }

    const previewEntry = data.templates.find((t) => t.key === previewKey);
    // The LA dashboard shows the decision and offer templates side by side
    const previewTitleId = `${kind}-template-preview-title`;
    const several = data.templates.length > 1;
    const shown = several
        ? data.templates.find((t) => t.key === shownKey) || data.templates[0]
        : null;

    return (
        <div>
            <h2 className="h5">{text.heading}</h2>
            <p>{text.intro}</p>
            <details className="mb-3">
                <summary>Fields you can insert</summary>
                <dl className="row mt-2 mb-0">
                    {data.fields.map((field) => (
                        <React.Fragment key={field.key}>
                            <dt className="col-sm-4">
                                {field.label}{' '}
                                <code className="text-break">{`{{${field.key}}}`}</code>
                            </dt>
                            <dd className="col-sm-8">{field.description}</dd>
                        </React.Fragment>
                    ))}
                </dl>
            </details>
            <PreviewLockNote locked={previewLocked} />
            {several && (
                <Form.Group
                    controlId={`${kind}-template-choice`}
                    className="mb-3 col-12 col-sm-8 col-lg-6"
                >
                    <Form.Label>Email to edit</Form.Label>
                    <Form.Select
                        value={shown.key}
                        onChange={(e) => setShownKey(e.target.value)}
                    >
                        {data.templates.map((entry) => (
                            <option key={entry.key} value={entry.key}>
                                {unsaved(entry)
                                    ? `${entry.label} (not saved)`
                                    : entry.label}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
            )}
            {(several ? [shown] : data.templates).map((entry) => {
                const { key } = entry;
                const note = notes[key];
                const body = bodies[key] ?? '';
                const boxId = `${kind}-template-${key}`;
                const busy = note?.kind === 'saving';
                const fieldKey = insertKey[key] || data.fields[0]?.key || '';
                const selectedField = data.fields.find(
                    (f) => f.key === fieldKey
                );
                const fieldHelpId = `${boxId}-field-help`;
                return (
                    <Card
                        as="section"
                        key={key}
                        className="border-0 shadow-sm mb-3"
                        aria-labelledby={`${boxId}-heading`}
                    >
                        <Card.Header>
                            <h3
                                id={`${boxId}-heading`}
                                className="h6 fw-bold mb-0"
                            >
                                {entry.label}
                            </h3>
                        </Card.Header>
                        <Card.Body>
                            <p className="mb-1 fw-semibold">
                                The email starts with:
                            </p>
                            {entry.fixedParagraphs.map((paragraph, index) => (
                                <p key={index} className="mb-1 text-break">
                                    {paragraph}
                                </p>
                            ))}
                            <Form.Group controlId={boxId} className="mt-2">
                                <Form.Label>{`${entry.label} email text`}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={5}
                                    maxLength={data.maxLength}
                                    ref={(node) => {
                                        boxes.current[key] = node;
                                    }}
                                    value={body}
                                    onChange={(e) => edit(key, e.target.value)}
                                    aria-describedby={`${boxId}-status ${boxId}-count`}
                                />
                                <Form.Text id={`${boxId}-count`} as="div" muted>
                                    {`${body.length.toLocaleString('en-US')} of ${data.maxLength.toLocaleString('en-US')} characters`}
                                </Form.Text>
                                <Form.Text
                                    id={`${boxId}-status`}
                                    as="div"
                                    muted
                                >
                                    {entry.isDefault
                                        ? 'Program default'
                                        : formatEdited(entry)}
                                    {unsaved(entry) &&
                                        '. Your changes are not saved yet.'}
                                </Form.Text>
                            </Form.Group>
                            <div className="d-flex align-items-end gap-2 flex-wrap mt-2">
                                <Form.Group controlId={`${boxId}-field`}>
                                    <Form.Label className="visually-hidden">
                                        {`Field to insert in the ${entry.label} email`}
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={fieldKey}
                                        aria-describedby={
                                            selectedField?.description
                                                ? fieldHelpId
                                                : undefined
                                        }
                                        onChange={(e) =>
                                            setInsertKey((prev) => ({
                                                ...prev,
                                                [key]: e.target.value,
                                            }))
                                        }
                                    >
                                        {data.fields.map((field) => (
                                            <option
                                                key={field.key}
                                                value={field.key}
                                            >
                                                {field.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    aria-label={`Insert field in the ${entry.label} email`}
                                    onClick={() => insertField(key, fieldKey)}
                                >
                                    Insert field
                                </Button>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    aria-label={`Preview the ${entry.label} email`}
                                    onClick={() => openPreview(key)}
                                >
                                    Preview
                                </Button>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    ref={(node) => {
                                        saveButtons.current[key] = node;
                                    }}
                                    onClick={() => save(key)}
                                    disabled={busy || previewLocked}
                                    aria-describedby={
                                        previewLocked
                                            ? PREVIEW_LOCK_NOTE_ID
                                            : undefined
                                    }
                                >
                                    {`Save ${entry.label} template`}
                                </Button>
                                {!entry.isDefault && (
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        aria-label={`Reset to the default: ${entry.label} email`}
                                        onClick={() => reset(key)}
                                        disabled={busy || previewLocked}
                                        aria-describedby={
                                            previewLocked
                                                ? PREVIEW_LOCK_NOTE_ID
                                                : undefined
                                        }
                                    >
                                        Reset to the default
                                    </Button>
                                )}
                                {unsaved(entry) && (
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        aria-label={`Discard the changes to the ${entry.label} email`}
                                        onClick={() => discard(entry)}
                                        disabled={busy}
                                    >
                                        Discard changes
                                    </Button>
                                )}
                            </div>
                            {selectedField?.description && (
                                <Form.Text
                                    id={fieldHelpId}
                                    as="div"
                                    muted
                                    className="mt-1"
                                >
                                    {`${selectedField.label}: ${selectedField.description}`}
                                </Form.Text>
                            )}
                            <div role="status" className="mt-2">
                                {note?.kind === 'saved' && 'Saved.'}
                                {note?.kind === 'reset' &&
                                    'Back to the default.'}
                            </div>
                            {note?.kind === 'error' && (
                                <Alert variant="danger" className="mt-2 mb-0">
                                    {note.text}
                                </Alert>
                            )}
                        </Card.Body>
                    </Card>
                );
            })}
            <Modal
                show={previewOpen}
                onHide={closePreview}
                size="lg"
                scrollable
                aria-labelledby={previewTitleId}
            >
                <Modal.Header closeButton>
                    <Modal.Title id={previewTitleId} as="h2" className="h5">
                        {`Preview: ${previewEntry?.label ?? ''} email`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        This shows the text in the box now, saved or not, with
                        sample values in place of the fields. Nothing is sent.{' '}
                        {text.previewEmpty}
                    </p>
                    {previewStatus === 'error' && previewError ? (
                        <Alert variant="warning" className="mb-0">
                            {previewError}
                        </Alert>
                    ) : (
                        <DecisionEmailPreview
                            emails={previewEmails}
                            status={previewStatus}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={closePreview}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default DecisionEmailTemplates;
